/**
 * Return to India Page
 * Editorial layout. No icons. No template patterns.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { formatPrice, formatDuration } from '../lib/utils';
import Button from '../components/common/Button';
import { PageLoader } from '../components/common/LoadingSpinner';
import { Container, Section, Spacer } from '../components/primitives';
import { Headline, Title, BodyLarge, Body, Label } from '../components/primitives/Typography';
import type { Service } from '../types';

export default function ReturnToIndia() {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const res = await api.getCategory('return-to-india');
        setServices(res.services);
      } catch (err) {
        console.error('Failed to load services:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  if (loading) return <PageLoader />;

  return (
    <main>
      {/* ========== Hero ========== */}
      <Section size="lg" className="pt-20 md:pt-32 pb-16 md:pb-24">
        <Container>
          <div className="max-w-3xl">
            <Label>Return to India</Label>
            <Headline className="mt-4">
              Tax planning, school admissions, and healthcare setup for returning NRIs.
            </Headline>
            <BodyLarge className="mt-8">
              We work with tech professionals who have been in the US for 8+ years and are
              planning to return to India within the next 1–3 years.
            </BodyLarge>
          </div>
        </Container>
      </Section>

      {/* ========== The Problem ========== */}
      <Section background="subtle">
        <Container>
          <div className="grid md:grid-cols-2 gap-16 md:gap-24">
            <div>
              <Label>The problem</Label>
              <Title className="mt-4">
                Returning to India is not just a move. It's a financial restructuring.
              </Title>
            </div>
            <div className="space-y-6">
              <Body>
                After 10+ years in the US, you've accumulated 401(k)s, RSUs, real estate,
                and complex tax obligations. A poorly planned return can cost you lakhs in
                unnecessary taxes and missed opportunities.
              </Body>
              <Body>
                Most people rely on advice from friends who returned years ago, or from
                professionals who don't understand both tax systems. The stakes are too high
                for guesswork.
              </Body>
            </div>
          </div>
        </Container>
      </Section>

      {/* ========== What We Help With ========== */}
      <Section>
        <Container>
          <div className="max-w-3xl">
            <Label>What we help with</Label>
            <Title className="mt-4">Three areas that matter most.</Title>
          </div>

          <div className="mt-16 space-y-0 divide-y divide-border">
            {/* Tax Planning */}
            <div className="py-12 md:py-16 grid md:grid-cols-12 gap-8">
              <div className="md:col-span-4">
                <h3 className="text-h3 font-medium text-text-primary">Tax Planning</h3>
              </div>
              <div className="md:col-span-8 space-y-4">
                <Body>
                  US-India tax optimization starting 2 years before your move. We help you
                  understand the timing of RSU vesting, 401(k) withdrawals, capital gains
                  realization, and the transition from US to Indian tax residency.
                </Body>
                <ul className="space-y-2 text-base text-text-secondary">
                  <li>FBAR and FATCA compliance</li>
                  <li>401(k) and IRA strategy</li>
                  <li>RSU vesting optimization</li>
                  <li>Capital gains timing</li>
                  <li>Treaty benefit utilization</li>
                </ul>
              </div>
            </div>

            {/* School Planning */}
            <div className="py-12 md:py-16 grid md:grid-cols-12 gap-8">
              <div className="md:col-span-4">
                <h3 className="text-h3 font-medium text-text-primary">School Admissions</h3>
              </div>
              <div className="md:col-span-8 space-y-4">
                <Body>
                  Children from American schools struggle with Indian curricula. The right
                  school depends on your child's age, the curriculum they've been in, and
                  how long you've been away. We match families with schools that work.
                </Body>
                <ul className="space-y-2 text-base text-text-secondary">
                  <li>IB, IGCSE, CBSE curriculum matching</li>
                  <li>School shortlisting by location</li>
                  <li>Admission timeline planning</li>
                  <li>Entrance test preparation guidance</li>
                </ul>
              </div>
            </div>

            {/* Healthcare */}
            <div className="py-12 md:py-16 grid md:grid-cols-12 gap-8">
              <div className="md:col-span-4">
                <h3 className="text-h3 font-medium text-text-primary">Healthcare Setup</h3>
              </div>
              <div className="md:col-span-8 space-y-4">
                <Body>
                  Health insurance in India works differently. We help you understand the
                  options, find policies that cover pre-existing conditions, and identify
                  hospitals that meet the standards you're used to.
                </Body>
                <ul className="space-y-2 text-base text-text-secondary">
                  <li>Health insurance comparison</li>
                  <li>Pre-existing condition coverage</li>
                  <li>Hospital network evaluation</li>
                  <li>Specialist referral networks</li>
                </ul>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* ========== Consultation Options ========== */}
      {services.length > 0 && (
        <Section background="subtle">
          <Container>
            <div className="max-w-3xl">
              <Label>Consultations</Label>
              <Title className="mt-4">Choose based on where you are in planning.</Title>
            </div>

            <div className="mt-16 grid md:grid-cols-3 gap-px bg-border">
              {services.map((service) => (
                <div key={service.id} className="bg-surface p-8">
                  <div className="text-small text-text-tertiary">
                    {formatDuration(service.duration_minutes)}
                  </div>
                  <h3 className="mt-2 text-h4 font-medium text-text-primary">
                    {service.name}
                  </h3>
                  <p className="mt-4 text-base text-text-secondary min-h-[80px]">
                    {service.description}
                  </p>
                  <div className="mt-6">
                    <span className="text-h3 font-medium text-text-primary">
                      {formatPrice(service.price_inr)}
                    </span>
                  </div>
                  <div className="mt-6">
                    <Link to={`/book?service=${service.slug}`}>
                      <Button variant="secondary" className="w-full">
                        Book this
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </Container>
        </Section>
      )}

      {/* ========== FAQ ========== */}
      <Section>
        <Container narrow>
          <Label>Questions</Label>
          <Title className="mt-4">Frequently asked.</Title>

          <div className="mt-12 space-y-0 divide-y divide-border">
            {[
              {
                q: 'When should I start planning?',
                a: 'Ideally 2 years before your move. Tax optimization requires time—RSUs need to vest, 401(k) withdrawal strategies need planning, and school admissions have specific windows.',
              },
              {
                q: 'What happens to my 401(k)?',
                a: 'You have options: leave it in the US, roll into an IRA, or withdraw. Each has different tax implications depending on your residency status at the time. We model the scenarios for your specific situation.',
              },
              {
                q: 'Do I still file US taxes after moving?',
                a: 'US citizens and green card holders file US taxes regardless of where they live. The US-India tax treaty provides mechanisms to avoid double taxation, but they require proper planning.',
              },
              {
                q: 'How are consultations conducted?',
                a: 'Video call—Zoom or Google Meet. You receive a link after booking. Most clients are in US timezones, so we schedule accordingly.',
              },
            ].map((faq, i) => (
              <div key={i} className="py-8">
                <h3 className="text-h4 font-medium text-text-primary">{faq.q}</h3>
                <Body className="mt-3">{faq.a}</Body>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      {/* ========== CTA ========== */}
      <Section background="muted">
        <Container>
          <div className="max-w-2xl mx-auto text-center">
            <Title>
              Start with a 30-minute call.
            </Title>
            <Body className="mt-4">
              We'll understand your situation and tell you if we can help.
              No pressure, no sales pitch.
            </Body>
            <div className="mt-10">
              <Link to="/book">
                <Button variant="primary" size="lg">
                  Book a consultation
                </Button>
              </Link>
            </div>
          </div>
        </Container>
      </Section>

      <Spacer size="sm" />
    </main>
  );
}
